<template>
    <div class="h-full maps-schedules-stop-info-div" :class="{'p-6': !lessPadding}">
        <div role="status" aria-live="polite">
            <div class="sr-only">
                <span v-if="loading">Loading stops</span>
            </div>
        </div>
        <div v-if="loading" class="flex items-center justify-center h-full">
            <Spinner class="text-primary w-8 h-8"/>
        </div>
        <div v-else class="space-y-4">
            <div v-for="stop in stopsInfo" :key="stop.code" class="px-4 relative">
                <div class="flex flex-col justify-between">
                    <div>
                        <div class="text-xl text-black font-bold">
                            <h2 v-if="stop.name">{{ stop.name }}</h2>
                        </div>
                        <div class="mt-2 text-base text-gray-700 font-normal flex justify-between">
                            <div class="stop-code-div">
                                Stop #{{ stop.code }}
                            </div>
                            <div class="marker-center-button-div flex items-center">
                                <TargetButton v-if="currentStopInfo" title="Go to stop" hover-color="#BB481B" focus-color="#BB481B"  @pan-to-location="panToLocation" />
                            </div>
                        </div>
                    </div>
                    <div v-if="stop.streetview" class="w-[5rem]">
                        <img class="w-full rounded" :src="stop.streetview" alt="" role="none"/>
                    </div>
                    <div class="relative top-5">
                        <div class="flex justify-between">
                            <button @click="reloadHandler()" v-if="showReloadButton" class="rounded-full bg-primary hover:scale-110 focus:ring-2 focus:ring-offset-2 focus:ring-primary p-2 text-white">Refresh</button>
                            <div class="mt-2" v-if="showReloadIntervalSelect">
                                <input type="checkbox" id="rt-auto-reload-checkbox" v-model="isAutoReloading" class="mr-2" />
                                <label for="rt-auto-reload-checkbox">Auto-refresh</label>
                            </div>
                        </div>
                        <progress v-if="showReloadIntervalProgress" :value="reloadRemainingPercentage" max="100" class="mt-3 h-0.5 w-full"></progress>
                    </div>
                </div>
                <div class="mt-8 pb-2 border-b border-gray-500 stop-times-trip-header">
                    <h3 v-for="trip in stop.trips" class="stop-times-trip-title font-bold text-base text-gray-700 inline-block mr-2">
                        <button
                            :class="{
                            'text-primary border-b-2 border-black': activeTrip === trip.title,
                            }"
                            @click="activeTrip = trip.title">{{ trip.title }}
                        </button>
                    </h3>
                </div>
                <template v-for="trip in stop.trips">
                    <div class="my-2" v-if="activeTrip === trip.title">
                        <template v-for="time in trip.times">
                            <div
                                class="border-transparent time-row border-y first:border-0 text-rm-default-color border-1 focus:outline-none focus:border-secondary"
                                tabindex="0" aria-label="Trip time: {{ time.value }} to {{ time.headsign }}">
                                <div class="flex py-2 space-x-4"
                                     :class="{ 'line-through decoration-primary-gray-800': !time.available }">
                                    <Tag v-if="getRoute(time)" :route="getRoute(time)" :disabled="!time.available"/>
                                    <div class="flex flex-col w-full"
                                         :class="{'text-primary-gray-800': !time.available}">
                                        <div class="flex-1 text-sm">{{ time.headsign }}</div>
                                        <div
                                            class="text-right text-sm flex justify-between"
                                        >
                                            <span v-if="time.isLate" :class="{'font-bold text-rm-delay-color': time.available, 'text-primary-gray-800': !time.available}">Delayed</span>
                                            <span v-else-if="time.isEarly" :class="{'font-bold text-rm-early-color': time.available, 'text-primary-gray-800': !time.available}">Early</span>
                                            <span v-else-if="time.isOnTime" :class="{'text-rm-on-time-color': time.available, 'text-primary-gray-800': !time.available}">On Time</span>
                                            <span v-else>Scheduled</span>
                                            <span class="time-display">
                                                    <span v-if="!time.isOnTime"
                                                          class="pr-2">{{ time.scheduledDepartureTimeFormatted }}</span>
                                                    <span
                                                        class="w-16 text-sm font-bold text-right"
                                                        :class="{
                                                            'text-primary-gray-800': !time.available,
                                                            'text-rm-delay-color': time.isLate && time.available,
                                                            'text-rm-early-color': time.isEarly && time.available,
                                                            'text-rm-on-time-color': time.isOnTime && time.available,
                                                        }"
                                                    >{{
                                                            time.realtime ? time.departureTimeFormatted : time.value
                                                        }}</span>
                                                </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <div class="text-right text-sm text-gray-500 font-normal mt-1">
                            Last updated at {{ trip.updatedAt }}
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import {usePage} from '@inertiajs/vue3';
import {mdiChevronLeft} from "@mdi/js";
import {defineComponent, inject, onBeforeUnmount, ref, watch} from 'vue';
import {betweenDates, formatAmPmTime, parseFromISO} from "../../utils/date";
import Icon from "../Icon";
import Spinner from "../Spinner";
import Tag from "../Tag";
import {get} from "lodash";
import {useRouteStore} from "../../stores/routes";
import StopInfoReloadIntervalSelect from "./StopInfoReloadIntervalSelect.vue";
import {ICONS} from "../../utils/icons";
import {storeToRefs} from "pinia";
import {useStopsStore} from "../../stores/stops";
import TargetButton from "./TargetButton.vue";
import {panToAdjustedLocation} from "../../utils/google";

export default defineComponent({
    name: 'MapsSchedulesStopInfo',
    computed: {
        ICONS() {
            return ICONS
        }
    },
    components: {TargetButton, StopInfoReloadIntervalSelect, Spinner, Tag, Icon},
    props: {
        route: {type: [Object, null], required: true},
        stops: {type: Array, required: true},
        showReloadButton: {type: Boolean, required: false, default: true},
        showReloadIntervalSelect: {type: Boolean, required: false, default: true},
        showReloadIntervalProgress: {type: Boolean, required: false, default: true},
        lessPadding: {type: Boolean, required: false, default: false}
    },

    setup(props) {
        // @todo Refactor so we don't need to use usePage() all over the place.
        const featureFlags = usePage().props.global.feature_flags;

        const reloadIntervalValues = [
            {
                seconds: 30000,
                label: '30s'
            }, {
                seconds: 60000,
                label: '1m'
            }, {
                seconds: 3 * 60000,
                label: '3m'
            }, {
                seconds: 5 * 60000,
                label: '5m'
            }
        ];
        const currentReloadInterval = ref(reloadIntervalValues[0].seconds)
        const selectedReloadInterval = ref(reloadIntervalValues[0].label)
        const isAutoReloading = ref(true)
        const remainingReloadTime = ref(0);
        const reloadStartTime = ref(0);
        const reloadRemainingPercentage = ref(0);

        let autoReloadInterval = null;
        let updateReloadProgressInterval = null;

        const api = inject('api');
        const routeStore = useRouteStore();
        const stopsStore = useStopsStore();
        const {currentStopInfo, mainGoogleMapRef} = storeToRefs(stopsStore)
        const stopsInfo = ref([]);
        const loading = ref(false);
        const googleApiKey = usePage().props.global.google.maps_api_key;

        const activeTrip = ref('');

        /**
         * Get information about stops.
         */
        async function getStopsInfo() {
            loading.value = true;
            stopsInfo.value = [];

            const arrivalTimesForMultipleDirections = get(featureFlags,
                'route_map_enable_arrival_times_for_multiple_directions');

            // We are iterating over stops because Route Map already provides
            // an array of selected stops, but for now only one is actually selected.
            for (const stop of props.stops) {
                const stopInfoResponse = await api.getStopInfo({
                    stopCode: stop.code,
                });

                if (!arrivalTimesForMultipleDirections) {
                    stopInfoResponse.data = stopInfoResponse.data.splice(0, 1);
                }

                let multiDirectionStopInfo = {};
                stopInfoResponse.data = stopInfoResponse.data ?? [];

                for (const stopInfoData of stopInfoResponse.data) {
                    const realtimeInfoResponseParams = {
                        stopId: stopInfoData.id
                    }

                    if(routeStore.routes.selected.length === 0){
                        realtimeInfoResponseParams.routeName = props.route.id
                    }

                    const realtimeInfoResponse = await api.getStopRealtimeInfo(realtimeInfoResponseParams);

                    const updatedAt = formatAmPmTime(realtimeInfoResponse.summary.generated_on_server_iso8601);
                    const streetview = (stopInfoData?.extra?.ext_photo?.trim()?.length > 0)
                        ? stopInfoData?.extra.ext_photo
                        :
                        `https://maps.googleapis.com/maps/api/streetview?size=160x120&location=${stopInfoData?.latitude},${stopInfoData?.longitude}&key=${googleApiKey}&source=outdoor`;

                    // Get the list of alerts affecting the stop
                    const alerts = (routeStore.alerts?.data || []).filter((alert) => {
                        if (alert.effect !== 'NO_SERVICE' && alert.effect !== 'IMPORTED_REDUCED_SERVICE') {
                            return false;
                        }

                        // Check alerts from GTFS Alerts Feed
                        const matchesEntities = Boolean((alert.entities || []).find((alertEntity) => {
                            return alertEntity.routeId.toLowerCase() === props.route.id.toLowerCase()
                                && (
                                    alertEntity.stops.length === 0
                                    || alertEntity.stops.some(alertStop => alertStop.stopId?.toString() ===
                                        stop.id?.toString())
                                );
                        }));

                        if (matchesEntities) {
                            return true;
                        }

                        //Check other alerts
                        if (!alert.routes.find(r => r.id.toLowerCase() === props.route.id.toLowerCase())) {
                            return false;
                        }

                        return Boolean(
                            (alert.stops || []).find(alertStop => alertStop.code?.toString() === stop.code?.toString())
                        );
                    });

                    multiDirectionStopInfo = getMultiDirectionStopInfo(
                        multiDirectionStopInfo,
                        updatedAt,
                        streetview,
                        alerts,
                        stopInfoData,
                        arrivalTimesForMultipleDirections
                    );

                    // Iterate over times and add them to the directions accordingly
                    const times = (realtimeInfoResponse?.data || []);

                    for(const time of times){
                        if(!multiDirectionStopInfo[time.directionId]) {
                            console.error(`No direction found on route ${time.routeId} for direction "${time.directionId}" at stop "${stop.code}"`);
                            continue
                        }

                        let addedTime = {
                            routeId: time.routeId,
                            headsign: time.headsign,
                            date: time.departureTime,
                            value: time.departureTimeFormatted.toUpperCase(),
                            unixtime: parseFromISO(time.departureTime),
                            realtime: time.realtime,
                            available: alerts.findIndex(alert => {
                                if (alert.effect !== 'NO_SERVICE' && alert.effect !== 'IMPORTED_REDUCED_SERVICE') {
                                    return false;
                                }

                                const depTime = parseFromISO(time.departureTime);

                                const activeRange = alert.activeRanges.some(range => {
                                    const activeFrom = (range.from) ? parseFromISO(range.from, 'UTC') : null;
                                    const activeTo = (range.to) ? parseFromISO(range.to, 'UTC') : null;

                                    return betweenDates(depTime, activeFrom, activeTo);
                                });

                                if (!activeRange) {
                                    return false;
                                }

                                // Check alerts from GTFS Alerts Feed
                                const matchesEntities = alert.entities.some(alertEntity => {
                                    return alertEntity.trips.some(alertTrip => time.tripId.toString() ===
                                        alertTrip.toString())
                                });

                                if (matchesEntities) {
                                    return true;
                                }

                                return (alert.stops || []).some(alertStop => alertStop.code?.toString() ===
                                    stop.code.toString());
                            }) === -1,
                        };

                        if (time.realtime) {
                            // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
                            const shortTime = new Intl.DateTimeFormat("en-US", {
                                timeStyle: "short",
                            });

                            const {departureTime, scheduledDepartureTime} = time;
                            let msDelay = Date.parse(departureTime) - Date.parse(scheduledDepartureTime);
                            let delay = msDelay / 1000; // The delay in seconds

                            let delayAbsVal = Math.abs(delay);
                            let isEarly = false;
                            let isLate = false;
                            let isOnTime = false;
                            if (delayAbsVal >= 60) {
                                if (delay < 0) {
                                    isEarly = true;
                                } else {
                                    isLate = true;
                                }
                            } else {
                                isOnTime = true;
                            }

                            const scheduledDepartureTimeFormatted = shortTime.format(new Date(scheduledDepartureTime));
                            const departureTimeFormatted = shortTime.format(new Date(departureTime));

                            addedTime = {
                                ...addedTime,
                                isEarly,
                                isLate,
                                isOnTime,
                                scheduledDepartureTimeFormatted,
                                departureTimeFormatted,
                            };
                        }

                        multiDirectionStopInfo[time.directionId].times.push(addedTime);
                    }
                }

                // Sort times of multi direction stop info. Filter directions without time because
                // there are stops (usually first and last) that indicate to have both directions
                // in its properties, but there are only times available for one of them.
                const sortedTrips = Object.values(multiDirectionStopInfo)
                    .filter((item) => item.times.length > 0)
                    .map((item) => ({
                        ...item,
                        times: item.times.sort((a, b) => a.unixtime - b.unixtime)
                    }));

                stopsInfo.value.push({
                    code: stop.code,
                    name: stop.name,
                    trips: sortedTrips,
                });
                activeTrip.value = sortedTrips[0]?.title
            }

            loading.value = false;
        }

        function getMultiDirectionStopInfo(
            multiDirectionStopInfo,
            updatedAt,
            streetview,
            alerts,
            stopInfoData,
            arrivalTimesForMultipleDirections
        ){
            // Get the list of directions available on this route, including their alerts.
            let dirs;

            if(routeStore.routes.selected.length === 0){
                dirs = stopInfoData.routes.find(
                    (r) => r.routeId.toLowerCase() === props.route.id.toLowerCase()
                )?.directionIds || [];
            } else {
                dirs = routeStore.routes.selected.reduce((directionIds, selectedRoute) => {
                    const selectedStopInfoRoute = stopInfoData.routes.find(
                        (r) => r.routeId.toLowerCase() === selectedRoute.id.toLowerCase()
                    );
                    return [
                        ...directionIds,
                        ...selectedStopInfoRoute?.directionIds
                    ];
                }, []);
            }

            dirs = (arrivalTimesForMultipleDirections)
                ? dirs
                : dirs.slice(0, 1);

            dirs.forEach((directionId) => {
                const directionName = get(props.route, `direction${directionId}Name`);

                if (!directionName) {
                    console.error(`No direction found for direction "${directionId}" at stop "${stop.code}"`);
                    return;
                }

                if (!multiDirectionStopInfo[directionId]) {
                    multiDirectionStopInfo[directionId] = {
                        alerts,
                        updatedAt,
                        streetview,
                        directionId,
                        title: arrivalTimesForMultipleDirections ? directionName : "Next Ride",
                        times: [],
                    }
                }
            }, {});

            return multiDirectionStopInfo;
        }

        function updateReloadProgress() {
            const elapsedTime = Date.now() - reloadStartTime.value;
            remainingReloadTime.value = currentReloadInterval.value - elapsedTime;
            reloadRemainingPercentage.value = Math.round((elapsedTime / currentReloadInterval.value) * 100)
        }

        function reloadHandler() {
            getStopsInfo();
            setReloadTimer();
        }

        function startReloadProgress() {
            updateReloadProgressInterval = window.setInterval(() => {
                updateReloadProgress();
            }, 1000)
        }

        function clearReloadProgress() {
            if (updateReloadProgressInterval) {
                window.clearInterval(updateReloadProgressInterval);
                updateReloadProgressInterval = null;
            }
        }

        function setReloadTimer() {
            reloadRemainingPercentage.value = 0;
            if (autoReloadInterval) {
                window.clearInterval(autoReloadInterval)
                autoReloadInterval = null;
            }
            clearReloadProgress();

            if (isAutoReloading.value) {
                reloadStartTime.value = Date.now();
                autoReloadInterval = window.setInterval(() => {
                    reloadStartTime.value = Date.now();
                    getStopsInfo();
                }, currentReloadInterval.value)
                startReloadProgress();
            }
        }

        function getRoute({ routeId }) {
            if(props.route.id === routeId){
                return routeStore.routes.current
            }
            return routeStore.routes.selected.find( r => r.id.toLowerCase() === routeId.toLowerCase())
        }

        function panToLocation(){
            panToAdjustedLocation(currentStopInfo.value.location, mainGoogleMapRef)
        }

        watch(
            () => props.stops, () => {
                setReloadTimer()
                getStopsInfo()
            },
            {immediate: true}
        );

        watch(isAutoReloading, () => {
            setReloadTimer();
        }, {immediate: true})

        watch(selectedReloadInterval, () => {
            currentReloadInterval.value = reloadIntervalValues.find((reloadValue) => {
                return reloadValue.label === selectedReloadInterval.value;
            }).seconds
            setReloadTimer();
        });

        onBeforeUnmount(() => {
            // watchers do not appear to be available here
            isAutoReloading.value = false
            setReloadTimer()
        })

        return {
            loading,
            stopsInfo,
            reloadHandler,
            reloadIntervalValues,
            reloadRemainingPercentage,
            currentReloadInterval,
            selectedReloadInterval,
            isAutoReloading,
            mdiChevronLeft,
            remainingReloadTime,
            activeTrip,
            getRoute,
            currentStopInfo,
            panToLocation
        }
    }
});
</script>

<style scoped>

progress {
    color: #591769;
    background-color: lightgray;
}

/* Progress bar background color */
progress::-webkit-progress-bar {
    background-color: lightgray;
    width: 100%;
}

/* Progress bar main color */
progress::-moz-progress-bar {
    background-color: #591769 !important;
}

progress::-webkit-progress-value {
    background-color: #591769 !important;
}
</style>
